import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Trash, Undo } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { CustomModal } from '@/shared/components/ui/modal';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { usePageActions } from '@/features/pages/hooks/usePageActions';
import { useArchivedPages } from '@/features/pages/hooks/useArchivedPages';

const TrashBox = ({ collectionId }: { collectionId?: string }) => {
  const { data: archivedPages = [] } = useArchivedPages(collectionId);
  const { unarchivePage, deletePage } = usePageActions();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [toDelete, setToDelete] = useState<{ id: string; title: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredArchivedPages = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return archivedPages;
    return archivedPages.filter(page => page.title.toLowerCase().includes(normalizedQuery));
  }, [archivedPages, searchQuery]);

  const onRestore = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>, documentId: string) => {
      event.stopPropagation();
      await unarchivePage(documentId);
    },
    [unarchivePage]
  );

  const initiateDelete = (id: string, title: string) => {
    setToDelete({ id, title });
    setIsModalOpen(true);
  };

  const onRemove = useCallback(
    async (documentId: string) => {
      await deletePage(documentId);
      setToDelete(null);
      setIsModalOpen(false);
    },
    [deletePage]
  );

  return (
    <section className="text-sm mt-2 pb-2">
      <div className="flex items-center gap-x-1 p-2">
        <Search className="h-4 w-4" />
        <Input
          onChange={e => {
            setSearchQuery(e.target.value);
          }}
          value={searchQuery}
          className="h-7 bg-secondary px-2 focus-visible:ring-transparent"
          placeholder="Filter by title..."
          aria-label="Filter notes"
        />
      </div>
      <div className="mt-2 px-1 pb-1 max-h-[300px] overflow-y-auto">
        {filteredArchivedPages.length === 0 && (
          <p className="pb-2 text-center text-xs text-muted-foreground">No notes found.</p>
        )}
        {filteredArchivedPages.map(document => (
          <div
            key={document.id}
            className="flex w-full items-center justify-between rounded-sm hover:bg-accent"
          >
            <Link
              to={
                document.collectionId
                  ? `/collection/${document.collectionId}/page/${document.id}`
                  : `/page/${document.id}`
              }
              className="truncate pl-2 flex-grow"
            >
              {document.title}
            </Link>
            <div className="flex items-center">
              <button
                onClick={e => {
                  void onRestore(e, document.id);
                }}
                className="rounded-sm p-2 hover:bg-secondary"
                aria-label="Restore Document"
              >
                <Undo className="h-4 w-4 text-muted-foreground " />
              </button>
              <button
                className="rounded-sm p-2 hover:bg-secondary"
                aria-label="Delete Permanently"
                onClick={() => {
                  initiateDelete(document.id, document.title);
                }}
              >
                <Trash className="h-4 w-4 text-muted-foreground " />
              </button>
            </div>
          </div>
        ))}
      </div>
      {toDelete && (
        <CustomModal
          title="Confirm Delete"
          width={400}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
        >
          <Card className="bg-background flex flex-col p-6 pb-4">
            <h2 className="text-center text-lg mb-4">
              Are you sure you want to delete the{' '}
              <span className="font-semibold">"{toDelete.title}"</span> notes permanently?
            </h2>
            <div className="space-y-2 flex flex-col">
              <Button
                onClick={() => {
                  void onRemove(toDelete.id);
                }}
                variant={'destructive'}
                className="border-destructive text-destructive-foreground hover:bg-destructive hover:text-destructive-foreground"
              >
                Yes, delete these notes
              </Button>
              <Button
                onClick={() => {
                  setIsModalOpen(false);
                }}
                variant={'ghost'}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </CustomModal>
      )}
    </section>
  );
};

export default TrashBox;
